import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChartBar,
  Eye,
  XCircle,
  CreditCard,
  TrendUp,
  Lightning,
  Brain,
  Trophy,
  Lock,
  Clock,
  Target,
  SpinnerGap,
  Warning,
  CurrencyGbp,
  Percent,
  UsersThree,
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_LABELS = {
  feature_lock: { label: 'Feature Lock', icon: Lock, color: 'text-accent' },
  struggle: { label: 'Struggle', icon: Brain, color: 'text-purple-400' },
  success: { label: 'Success', icon: Trophy, color: 'text-success' },
  first_session_gate: { label: 'First Session', icon: Lightning, color: 'text-amber-500' },
  exit_intent: { label: 'Exit Intent', icon: Clock, color: 'text-amber-500' },
};

export const AdminPaywallPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data: res } = await axios.get(`${API}/admin/paywall/analytics`, { withCredentials: true });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load paywall analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Warning weight="fill" className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive font-bold">{error}</p>
        </div>
      </div>
    );
  }

  const { overview, trigger_breakdown, plan_breakdown, daily_trend, revenue } = data;

  // Find max daily views for chart scaling
  const maxDaily = Math.max(...(daily_trend || []).map(d => Math.max(d.views, d.dismissals, d.checkouts)), 1);

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="admin-paywall-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Admin</p>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Paywall Analytics</h1>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="paywall-overview">
        <MetricCard icon={Eye} label="Total Views" value={overview.total_views} color="text-blue-400" />
        <MetricCard icon={XCircle} label="Dismissals" value={overview.total_dismissals} color="text-amber-500" />
        <MetricCard icon={CreditCard} label="Checkouts" value={overview.total_checkouts} color="text-success" />
        <MetricCard icon={TrendUp} label="Conversion Rate" value={`${overview.conversion_rate}%`} color="text-accent" />
      </div>

      {/* Second row: User stats + Revenue */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={UsersThree} label="Tracked Users" value={overview.total_tracked_users} color="text-muted-foreground" />
        <MetricCard icon={Percent} label="Discount Eligible" value={overview.discount_eligible_users} color="text-purple-400" />
        <MetricCard icon={CurrencyGbp} label="Total Revenue" value={`£${(revenue.total_revenue || 0).toFixed(2)}`} color="text-success" />
        <MetricCard icon={CurrencyGbp} label="Discount Revenue" value={`£${(revenue.discount_revenue || 0).toFixed(2)}`} color="text-amber-500" />
      </div>

      {/* Trigger Breakdown Table */}
      <div className="bg-card border border-border rounded-sm mb-8" data-testid="trigger-breakdown">
        <div className="p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">Trigger Performance</h2>
          <p className="text-xs text-muted-foreground">Conversion rates by paywall trigger type</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">Trigger</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">Views</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">Dismissals</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">Checkouts</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {(trigger_breakdown || []).map((t) => {
                const config = TRIGGER_LABELS[t.trigger] || { label: t.trigger, icon: Target, color: 'text-muted-foreground' };
                const TIcon = config.icon;
                return (
                  <tr key={t.trigger} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TIcon weight="fill" className={cn('w-4 h-4', config.color)} />
                        <span className="font-medium">{config.label}</span>
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 font-mono">{t.views}</td>
                    <td className="text-right px-4 py-3 font-mono">{t.dismissals}</td>
                    <td className="text-right px-4 py-3 font-mono font-bold text-success">{t.checkouts}</td>
                    <td className="text-right px-4 py-3">
                      <span className={cn(
                        'font-bold text-xs px-2 py-0.5 rounded-sm',
                        t.conversion_rate > 10 ? 'bg-success/20 text-success' :
                        t.conversion_rate > 0 ? 'bg-amber-500/20 text-amber-500' :
                        'bg-muted text-muted-foreground'
                      )}>
                        {t.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
              {(!trigger_breakdown || trigger_breakdown.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No paywall events recorded yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-card border border-border rounded-sm p-4" data-testid="plan-breakdown">
          <h2 className="font-heading font-bold text-lg mb-4">Checkout by Plan</h2>
          {Object.keys(plan_breakdown || {}).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No checkouts yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(plan_breakdown).map(([plan, count]) => {
                const total = Object.values(plan_breakdown).reduce((a, b) => a + b, 0);
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const colors = {
                  standard: 'bg-blue-500',
                  premium: 'bg-accent',
                  one_time: 'bg-amber-500',
                };
                const labels = { standard: 'Core', premium: 'Pass Faster', one_time: 'One-Time Pass' };
                return (
                  <div key={plan}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{labels[plan] || plan}</span>
                      <span className="text-xs font-mono text-muted-foreground">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-sm overflow-hidden">
                      <div
                        className={cn('h-full rounded-sm transition-all', colors[plan] || 'bg-primary')}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-card border border-border rounded-sm p-4" data-testid="revenue-breakdown">
          <h2 className="font-heading font-bold text-lg mb-4">Revenue Summary</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
              <span className="text-sm">Total Transactions</span>
              <span className="font-bold font-mono">{revenue.total_txns || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-success/10 rounded-sm">
              <span className="text-sm">Full Price Revenue</span>
              <span className="font-bold font-mono text-success">£{(revenue.full_price_revenue || 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-sm">
              <span className="text-sm">Discount Revenue</span>
              <span className="font-bold font-mono text-amber-500">£{(revenue.discount_revenue || 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-sm">
              <span className="text-sm">Discount Transactions</span>
              <span className="font-bold font-mono text-purple-400">{revenue.discount_txns || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Trend Chart */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="daily-trend">
        <h2 className="font-heading font-bold text-lg mb-2">14-Day Trend</h2>
        <p className="text-xs text-muted-foreground mb-4">Views, dismissals, and checkouts per day</p>
        {(!daily_trend || daily_trend.length === 0) ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No data in the last 14 days</p>
        ) : (
          <div className="flex items-end gap-1 h-40">
            {daily_trend.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                {/* Tooltip */}
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-card border border-border rounded-sm p-2 text-[10px] hidden group-hover:block z-10 whitespace-nowrap shadow-lg">
                  <p className="font-bold">{day.date}</p>
                  <p className="text-blue-400">Views: {day.views}</p>
                  <p className="text-amber-500">Dismiss: {day.dismissals}</p>
                  <p className="text-success">Checkout: {day.checkouts}</p>
                </div>
                {/* Bars */}
                <div className="w-full flex gap-px" style={{ height: '100%', alignItems: 'flex-end' }}>
                  <div
                    className="flex-1 bg-blue-500/60 rounded-t-sm transition-all"
                    style={{ height: `${(day.views / maxDaily) * 100}%`, minHeight: day.views > 0 ? '2px' : '0' }}
                  />
                  <div
                    className="flex-1 bg-amber-500/60 rounded-t-sm transition-all"
                    style={{ height: `${(day.dismissals / maxDaily) * 100}%`, minHeight: day.dismissals > 0 ? '2px' : '0' }}
                  />
                  <div
                    className="flex-1 bg-success/60 rounded-t-sm transition-all"
                    style={{ height: `${(day.checkouts / maxDaily) * 100}%`, minHeight: day.checkouts > 0 ? '2px' : '0' }}
                  />
                </div>
                <span className="text-[8px] text-muted-foreground">{day.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 justify-center">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-blue-500/60" />
            <span className="text-[10px] text-muted-foreground">Views</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-amber-500/60" />
            <span className="text-[10px] text-muted-foreground">Dismissals</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-success/60" />
            <span className="text-[10px] text-muted-foreground">Checkouts</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-card border border-border p-4 rounded-sm" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <Icon weight="fill" className={cn('w-7 h-7', color)} />
      <div>
        <p className="font-heading font-bold text-2xl tracking-tight">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      </div>
    </div>
  </div>
);
